// MIGRACIÓN: Este archivo usa el nuevo Auth Service
// Backup del original guardado en AuthContext.jsx.backup

// AuthContext.jsx - Migrado al nuevo sistema
import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react'
import AuthService from './services/auth.service.js'
import TokenService from './services/token.service.js'
import BillingService from './services/billing.service.js'

// =============================================================================
// CONTEXTO DE TRANSICIÓN
// Este AuthContext mantiene compatibilidad con el sistema existente
// mientras migra gradualmente al nuevo Auth Service
// =============================================================================

// Tipos de acciones
const AuthActionTypes = {
  AUTH_LOADING: 'AUTH_LOADING',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_ERROR: 'AUTH_ERROR',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  UPDATE_COINS: 'UPDATE_COINS',
  UPDATE_USER: 'UPDATE_USER'
}

// Estado inicial
const initialState = {
  isAuthenticated: false,
  isLoading: true,
  sessionId: null,
  coins: 0,
  user: null,
  error: null
}

// Reducer
function authReducer(state, action) {
  switch (action.type) {
    case AuthActionTypes.AUTH_LOADING:
      return { ...state, isLoading: true, error: null }

    case AuthActionTypes.AUTH_SUCCESS:
      return {
        ...state,
        isAuthenticated: true,
        isLoading: false,
        sessionId: action.payload.sessionId || 'jwt_session',
        coins: action.payload.coins || 0,
        user: action.payload.user,
        error: null
      }

    case AuthActionTypes.AUTH_ERROR:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        sessionId: null,
        coins: 0,
        user: null,
        error: action.payload.error
      }

    case AuthActionTypes.AUTH_LOGOUT:
      return {
        ...state,
        isAuthenticated: false,
        isLoading: false,
        sessionId: null,
        coins: 0,
        user: null,
        error: null
      }

    case AuthActionTypes.UPDATE_COINS:
      return {
        ...state,
        coins: action.payload
      }

    case AuthActionTypes.UPDATE_USER:
      return {
        ...state,
        user: {
          ...(state.user || {}),
          ...(action.payload || {})
        }
      }

    default:
      return state
  }
}

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)

  // =============================================================================
  // INICIALIZACIÓN CON NUEVO AUTH SERVICE
  // =============================================================================
    const initializeAuth = useCallback(async () => {
      try {
        // 1. Verificar si hay token JWT válido
        const token = TokenService.getAccessToken()
      
        if (!token) {
          dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
          return
        }

        // 2. Verificar si el token está expirado
        if (TokenService.isJWTExpired()) {
          TokenService.clearAll()
          dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
          return
        }

        // 3. Obtener datos del usuario desde el JWT
        const userData = AuthService.getCurrentUser()

        if (!userData || !userData.user_id) {
          console.error('❌ [Auth] No se pudo obtener el user_id del JWT o localStorage.');
          TokenService.clearAll()
          dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
          return
        }

        // 4. MARCAR COMO AUTENTICADO INMEDIATAMENTE con datos básicos
        dispatch({
          type: AuthActionTypes.AUTH_SUCCESS,
          payload: {
            user: userData,
            coins: userData.coins || 0,
            sessionId: 'jwt_session'
          }
        })

        // 5. Cargar perfil completo en BACKGROUND (no bloquea)
        AuthService.getUserProfile()
          .then(fullProfile => {
            const completeUser = {
              ...userData,
              ...fullProfile,
              user_id: userData.user_id,
              account_id: userData.account_id,
              default_account_id: userData.default_account_id
            }
            
            dispatch({
              type: AuthActionTypes.UPDATE_USER,
              payload: completeUser
            })
          })
          .catch(profileError => {
            console.warn('Failed to fetch full profile:', profileError)
          })

      } catch (error) {
        console.error('Error inicializando autenticación:', error)
        dispatch({ type: AuthActionTypes.AUTH_ERROR, payload: { error: error.message } })
      }
    }, [])

  useEffect(() => {
    initializeAuth()
  }, [initializeAuth])

  // =============================================================================
  // MÉTODOS DE COMPATIBILIDAD CON SISTEMA EXISTENTE
  // =============================================================================

  /**
   * Autenticar usuario (compatible con sistema existente)
   * @param {string} newSessionId - Session ID (o se puede pasar datos de JWT)
   * @param {number} newCoins - Cantidad de coins
   */
  const authenticateUser = useCallback((newSessionId, newCoins = 0) => {
    // Si newSessionId es un objeto, viene del nuevo sistema
    if (typeof newSessionId === 'object') {
      // Manejar nueva estructura de respuesta
      if (newSessionId.success && newSessionId.data) {
        const { user, tokens } = newSessionId.data
        
        // Guardar tokens del nuevo sistema
        if (tokens) {
          TokenService.saveTokens(tokens)
          // Ya no guardamos user_data, se obtiene del JWT
        }
        
        dispatch({
          type: AuthActionTypes.AUTH_SUCCESS,
          payload: {
            user,
            sessionId: `jwt_${user.user_id}`,
            coins: user.coins || 0
          }
        })
      } else {
        // Estructura antigua por compatibilidad
        const { user, tokens, coins } = newSessionId
        
        // Guardar tokens del nuevo sistema
        if (tokens) {
          TokenService.saveTokens(tokens)
          // Ya no guardamos user_data, se obtiene del JWT
        }
        
        dispatch({
          type: AuthActionTypes.AUTH_SUCCESS,
          payload: {
            user,
            sessionId: `jwt_${user.user_id}`,
            coins: coins || 0
          }
        })
      }
    } else {
      // Sistema legacy
      dispatch({
        type: AuthActionTypes.AUTH_SUCCESS,
        payload: {
          sessionId: newSessionId,
          coins: newCoins,
          user: { legacy: true, sessionId: newSessionId }
        }
      })
    }
  }, [])

  /**
   * Logout (compatible con ambos sistemas)
   */
  const performLogout = useCallback(async () => {
    try {
      // Limpiar tokens JWT
      AuthService.logout()
      
      // Limpiar sessionId legacy si existe
      localStorage.removeItem('sessionId')
      document.cookie = 'sessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
      
      dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
      
      // Redirigir al login
      window.location.href = '/login'
    } catch (error) {
      console.error('❌ Error en logout:', error)
      // Forzar limpieza y redirección
      dispatch({ type: AuthActionTypes.AUTH_LOGOUT })
      window.location.href = '/login'
    }
  }, [])

  /**
   * Actualizar coins
   * @param {number} newCoins - Nueva cantidad de coins
   */
  const updateCoins = useCallback((newCoins) => {
    dispatch({
      type: AuthActionTypes.UPDATE_COINS,
      payload: newCoins
    })

    // Los coins se actualizarán en el servidor y volverán en el próximo JWT refresh
    // Ya no guardamos nada en localStorage
  }, [])

  /**
   * Refrescar coins consultando el balance real del billing_service.
   * Usa `currentAccountId` de localStorage (mismo que mantiene
   * OrganizationContext) para no acoplar AuthContext al contexto de
   * organización. Si no hay account o el fetch falla, no toca el state
   * — el header simplemente conserva el último valor conocido.
   */
  const refreshCoins = useCallback(async () => {
    try {
      const accountId = localStorage.getItem('currentAccountId')
      if (!accountId) return
      const result = await BillingService.getAccountBalance(accountId)
      if (result?.success) {
        const total = result.data?.totalBalance ?? 0
        dispatch({
          type: AuthActionTypes.UPDATE_COINS,
          payload: total
        })
      }
    } catch (e) {
      // Silent fail — global header sync should never throw or alert.
    }
  }, [])

  const updateUser = useCallback((partialUser) => {
    if (!partialUser || typeof partialUser !== 'object') return

    const currentUser = TokenService.getUserData() || {}
    const mergedUser = {
      ...currentUser,
      ...partialUser
    }

    // Evitar dispatch cuando no hay cambios reales para no disparar loops de efectos.
    const hasChanges = Object.keys(partialUser).some(
      (key) => currentUser[key] !== partialUser[key]
    )

    if (!hasChanges) {
      return
    }

    try {
      TokenService.saveUserData(mergedUser)
    } catch (error) {
      console.error('Failed to persist user data update:', error)
    }

    dispatch({
      type: AuthActionTypes.UPDATE_USER,
      payload: partialUser
    })
  }, [])

  // =============================================================================
  // NUEVOS MÉTODOS DEL AUTH SERVICE
  // =============================================================================

  /**
   * Login con el nuevo sistema
   * @param {string} email - Email del usuario
   * @param {string} password - Contraseña
   */
  const loginWithJWT = useCallback(async (email, password) => {
    dispatch({ type: AuthActionTypes.AUTH_LOADING })

    try {
      const result = await AuthService.login(email, password)
      
      // Verificar que el resultado es válido
      if (!result || !result.user) {
        dispatch({
          type: AuthActionTypes.AUTH_ERROR,
          payload: { error: 'No response from login service' }
        })
        throw new Error('No response from login service')
      }
      
      const user = result.user;
      
      if (!user || !user.user_id) {
        dispatch({
          type: AuthActionTypes.AUTH_ERROR,
          payload: { error: 'No user data in response' }
        })
        throw new Error('No user data in response')
      }
      
      // Obtener perfil completo del usuario para tener avatar y otros datos
      try {
        const fullProfile = await AuthService.getUserProfile()
        
        // Combinar datos del login con el perfil completo
        const completeUser = {
          ...user,
          ...fullProfile,
          // Asegurar que los campos críticos no se sobrescriban
          user_id: user.user_id,
          account_id: user.account_id,
          default_account_id: user.default_account_id
        }
        
        dispatch({
          type: AuthActionTypes.AUTH_SUCCESS,
          payload: {
            user: completeUser,
            sessionId: `jwt_${user.user_id}`,
            coins: completeUser.coins || user.coins || 0
          }
        })
      } catch (profileError) {
        console.warn('Failed to fetch full profile, using basic user data:', profileError)
        
        // Si falla el fetch del perfil, usar los datos básicos del login
        dispatch({
          type: AuthActionTypes.AUTH_SUCCESS,
          payload: {
            user: user,
            sessionId: `jwt_${user.user_id}`,
            coins: user.coins || 0
          }
        })
      }

      return result
    } catch (error) {
      dispatch({
        type: AuthActionTypes.AUTH_ERROR,
        payload: { error: error.message }
      })
      throw error
    }
  }, [])

  /**
   * Verificar estado de autenticación
   */
  const checkAuthStatus = useCallback(async () => {
    try {
      if (TokenService.hasValidToken()) {
        await AuthService.validateToken()
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }, [])

  // =============================================================================
  // VALORES DEL CONTEXTO
  // =============================================================================
  const contextValue = {
    // Estado compatible con sistema existente
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    sessionId: state.sessionId,
    coins: state.coins,
    user: state.user,
    error: state.error,

    // Métodos compatibles
    authenticateUser,
    logout: performLogout,
    updateCoins,
    refreshCoins,
    updateUser,

    // Nuevos métodos
    loginWithJWT,
    checkAuthStatus,
    
    // Servicios expuestos para uso avanzado
    authService: AuthService,
    tokenService: TokenService,

    // Información adicional
    hasValidToken: TokenService.hasValidToken.bind(TokenService),
    isUsingJWT: TokenService.hasValidToken(),
    isLegacyAuth: state.sessionId && !TokenService.hasValidToken(),
    isEmailVerified: TokenService.isEmailVerified ? TokenService.isEmailVerified.bind(TokenService) : () => true
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook para usar el contexto
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext